import FloorTreeViewModel from "../modelTreeViews/FloorTreeviewModel";
import BaseModel, { executePaste_Parameters } from "./BaseModel";
import BaseModelMap from "./BaseModelMap";
import BaseObject from "./BaseObject";
import Page from "./Page";
import Room from "./Room";
import { copyAllProperties, sortArrayByProperty } from "./utils";
import Cabinet from "./Cabinet";
import Logger from "../Logger";

const logger = new Logger();
//////////////////////////////////////////////////
import ObjectFactory from "./ObjectFactory";
import Button from "./Button";
import Consumer from "./Consumer";
import { PageTypes } from "./PageTypes";
import { itx } from "../translationItx/itx";
import type { C1_Floor } from "../../ConceptOne/src/model/C1_Floor";
import { C1_Wall } from "../../ConceptOne/src/model/C1_Wall";

ObjectFactory.registerClass("Floor", (data?: any, parent?: BaseModel) => {
  return new Floor({});
});
//////////////////////////////////////////////////
export default class Floor extends BaseModel {
  override get c1(): C1_Floor | undefined {
    return super.c1 as C1_Floor | undefined;
  }

  override set c1(newValue: C1_Floor | undefined) {
    super.c1 = newValue;
  }

  public get className(): string {
    return "Floor"; // no itx
  }

  public get isFloor(): boolean {
    return true;
  }

  _page: Page | undefined = undefined;

  // -----------------------------------------------------------------------------
  // picture
  // -----------------------------------------------------------------------------
  _picture: string | undefined;
  get picture(): string | undefined {
    return this._picture;
  }

  set picture(newValue: string | undefined) {
    if (this._picture === newValue) return;
    this.beforeChange("picture");
    this._picture = newValue;
    this.changed("picture");
  }

  // -----------------------------------------------------------------------------
  rooms = new BaseModelMap<Room>(Room, this, "r");
  /** Independent Concept One wall segments, separate from room boundary topology. */
  freeWalls = new BaseModelMap<C1_Wall>(C1_Wall, this, "freeWalls");

  // -----------------------------------------------------------------------------
  // cabinetId
  // -----------------------------------------------------------------------------
  _cabinetId = "";
  get cabinetId(): string {
    return this._cabinetId;
  }

  set cabinetId(newValue: string) {
    if (this._cabinetId === newValue) return;
    this.beforeChange("cabinetId");
    this._cabinetId = newValue;
    this.changed("cabinetId");
  }

  // -----------------------------------------------------------------------------
  waitForNewCabinet = false;

  getPersistentProperties(): string[] {
    return ["oref:page", "picture", "rooms", "freeWalls", "cabinetId", ...super.getPersistentProperties()]; // no itx
  }

  set page(newValue: Page | undefined) {
    this.beforeChange("page");
    if (newValue) {
      newValue.propertyId = "p";
    }
    this._page = newValue;

    this.changed("page");
  }

  get page(): Page | undefined {
    return this._page;
  }

  getOrCreatePage(): Page {
    if (this.page == undefined) {
      this.page = new Page({}, this);
      //
      // Set Default PageSize for Floors to A3
      //
      this.page.pagesize = {
        width: 42,
        height: 29.7,
      };
      this.page.parent = this;
      this.page.documentType.type = PageTypes.floorplan;
      this.page.documentType.name = state.translate("pageDocumentType.floorplan", "Etagenplan");
    }
    return this.page;
  }

  loadFromEstadaStrage = async (
    data: any,
    parent: BaseModel | undefined,
    loadData: (id: string | undefined, parentId: string | undefined) => any,
  ): Promise<boolean> => {
    // create instances of optional references

    const orefPage = data["oref:page"]; // no itx
    if (orefPage) {
      this.page = new Page({ id: orefPage }, this);
      logger.log(`load page ${orefPage}, created`);
    }

    // normal loading

    const res = await super.loadFromEstadaStorage(data, parent, loadData);
    return res;
  };

  emptyTagIsForbidden(): boolean {
    return state.floors.size > 1;
  }

  cabinetIsLoading = false;

  get cabinet(): Cabinet | undefined {
    if (this.cabinetId != "") {
      return this.state.objectRegistry.getObject(this.cabinetId);
    }
    return undefined;
  }

  fromJSON(data: any, parent: BaseObject | undefined = undefined): void {
    if (data.page) {
      this.page = new Page({}, this);
    }
    super.fromJSON(data, parent);
  }

  delete(): Array<BaseObject> {
    this.isDeleting = true;

    var res = new Array<BaseObject>();

    // delete all child rooms
    res = res.concat(this.rooms.deleteAllItems());
    res = res.concat(this.freeWalls.deleteAllItems());

    //delete page
    if (this.page) {
      res = res.concat(this.page.delete());
    }

    // delete this
    res = res.concat(super.delete());

    return res;
  }

  canBeDeletedInteractive(): boolean {
    // A read-only availability check must not create the general room on an empty floor.
    return this.rooms.size === 0;
  }

  getDeleteConfirmText(): string | undefined {
    if (!this.canBeDeletedInteractive()) {
      const T1 = this.fullTag;
      let T2 = "";
      if (this.rooms.size > 0) {
        T2 = `${this.rooms.size} ${itx("components.plural.Room")}`;
      } else {
        const buttonCount = this.getRoomGeneral().buttons.size;
        const consumerCount = this.getRoomGeneral().consumers.size;
        if (buttonCount > 0 && consumerCount > 0) {
          T2 = `${buttonCount} ${itx("components.plural.Button")} ${itx("and")} ${consumerCount} ${itx("components.plural.Consumer")}`;
        } else if (buttonCount > 0) {
          T2 = `${buttonCount} ${itx("components.plural.Button")}`;
        } else if (consumerCount > 0) {
          T2 = `${consumerCount} ${itx("components.plural.Consumer")}`;
        }
      }

      return state.translate("Floor.T1StillHasT2", "T1 hat noch T2", {
        T1: `${T1}`,
        T2: T2,
      });
    } else {
      return super.getDeleteConfirmText();
    }
  }
  canBeEditedInteractive(): boolean {
    return false;
  }

  getAllTagsFromSameTypeWithoutOwn(): string[] | undefined {
    const res = new Array<string>();

    state.floors.forEach((floor) => {
      if (this.tag != floor.tag) {
        res.push(floor.tag);
      }
    });
    return res;
  }

  constructor(data?: any, parent?: BaseObject) {
    super(data, parent);
    copyAllProperties(data, this);
    this.treeViewModel = new FloorTreeViewModel(this);
  }

  getParentChildrenGroup(): Array<BaseModel> | undefined {
    return state.floors.sortBy("tag");
  }

  //////////////////////////////////////////////////////
  ///////////// for copy

  getExtendedCopyCollection(startItem: BaseModel): Array<BaseModel> {
    const res = new Array<BaseModel>();
    this.rooms.forEach((room) => {
      res.push(room);
    });

    //
    // Wenn wir einen Floor kopieren und der ein zugeordnetes Cabinets hat
    // erstellen wir auch eine Kopie dieses Cabinets
    //
    if (this.cabinet) {
      res.push(this.cabinet);
    }

    return res;
  }
  //////////////////////////////////////////////////////
  ///////////// for paste

  mapAfterCopy() {
    if (!this.mapAfterCopyDone) {
      super.mapAfterCopy();
      this.rooms.mapAfterCopy();
      this.freeWalls.mapAfterCopy();
      if (this.cabinetId) {
        this.cabinetId = state.objectMapping.getMappedId(this.cabinetId) || "";
      }
      this.cabinet?.mapAfterCopy();
      this.page?.mapAfterCopy();
    }
  }

  async pasteItem(params: { item: BaseModel; allItems: Array<BaseModel> }): Promise<Array<BaseModel>> {
    const { item } = params;
    const toHandleFromRoomGeneral = ["Button", "Consumer", "Memo"]; // no itx
    if (item.className == "Floor") {
      return await item.executePaste({
        parent: state.floors,
        isCopyRootObj: true,
      });
    } else if (item.className == "Room") {
      return await item.executePaste({ parent: this, isCopyRootObj: true });
    } else if (item.className == "Cabinet") {
      // to be done
      logger.log("paste cabinet in floor still not supported");
    } else if (toHandleFromRoomGeneral.includes(item.className)) {
      // handle consumer etc.
      const roomGeneral = this.getRoomGeneral();
      return await roomGeneral.pasteItem(params);
    }

    return [];
  }

  async executePaste(params: executePaste_Parameters): Promise<Array<BaseModel>> {
    const { parent, isCopyRootObj } = params;
    var undoObjects: Array<BaseModel> = [this];

    // set parent
    this.parent = state.floors;
    state.floors.add(this);

    if (this.cabinet && !this.cabinet.isMainCabinet()) {
      undoObjects = await this.cabinet.executePaste({ parent: state.cabinets, isCopyRootObj: false });
      this.cabinet.updateTagInPaste(state.cabinets);
    }

    if (isCopyRootObj || state.projectMetadata.uniqueTagsForProject) {
      this.tag = "";
      this.updateTagInPaste(state.floors);
    }
    if (isCopyRootObj) {
      this.updateNameInPaste(state.floors);
    }
    for (let room of this.rooms.asArray()) {
      undoObjects.concat(await room.executePaste({ parent: this, isCopyRootObj: false }));
    }

    return undoObjects;
  }

  get allButtons(): Array<Button> {
    let res = new Array<Button>();
    for (const room of this.rooms) {
      res = res.concat(room.allButtons);
    }
    return sortArrayByProperty(res, "fullTag");
  }

  getRoomGeneral(): Room {
    let roomGeneal = this?.rooms.getItemByTag("");
    if (!roomGeneal) {
      roomGeneal = new Room({ tag: "", name: "" }, this);
      this?.rooms.add(roomGeneal);
    }
    return roomGeneal;
  }

  allConsumers(): Array<Consumer> {
    const res = new Array<Consumer>();

    for (const room of this.rooms) {
      for (const consumer of room.allConsumers()) {
        res.push(consumer);
      }
    }

    return sortArrayByProperty(res, "fullTag");
  }

  cumulateChildObjectsErrorStatus(): boolean {
    let res = this.rooms.cumulateChildObjectsErrorStatus();
    res = this.page?.hasErrors || this.page?.cumulateChildObjectsErrorStatus() || res;
    return res;
  }

  consistencyCheck(parent: BaseModel) {
    super.consistencyCheck(parent);
    this.rooms.consistencyCheck(this);
    this.freeWalls.consistencyCheck(this);
    this.page?.consistencyCheck(this);
    if (this.cabinetId) {
      const cab = this.cabinet;
      if (!cab) {
        this.logConsistencyError(`cabinet with id ${this.cabinetId} not found`);
        this.cabinetId = "";
      }
    } else {
      this.logConsistencyError(`floor ${this.fullTag} has no cabinet assigned`, "floorWithoutCabinet");
    }
  }

  get hasRoomsWithoutPlacementInFloorPlanAndNoBackgroundImg(): number {
    let res = 0;
    for (const room of this.rooms.values) {
      if (!room.roomtoFloorDrawingTransform && !room.isFloorGeneralRoom && !room.page?.backgroundImgUrl) {
        res++;
      }
    }

    return res;
  }

  mergeAllFrom(fromFloor: Floor) {
    const allMergeRooms = fromFloor.rooms.asArray();
    allMergeRooms.forEach((room) => room.moveToNewParent(this));
  }
}
